import { CITY_ROUTES } from '../cityRoutes';
import { roadMapBridge, lineClear } from '../render/roadMapBridge';
import { routeBetween } from './cityPath';

// cityDrive — an artist's car ride between two places, along the city's lines.
//
// Khari, 2026-10-23, on how the artists should drive: *"we may have structure
// for these with the city map lines. they are editable, the cars follow them,
// and the camera can as well."* The route lines (src/cityRoutes.js, edited in
// ?routes=edit) are the streets the traffic drives. Joined wherever two of them
// cross or run together, they make a small network; a ride is the shortest way
// along it.
//
// ⚠ THE SHIPPED LINES, NEVER A LOCAL EDIT. CityLife lets a saved localStorage
// override win per machine; a story's drive must be the same for every viewer,
// so this reads CITY_ROUTES as shipped. An edit counts once it is pasted in.
//
// A drive segment (API cityTimeline `k: 'drive'`) is laid out in three legs:
//   on foot from the scene to the nearest point on a line,
//   by car along the lines,
//   on foot from the car to the next scene.
// The server only books the time window; the legs are fitted into it here, the
// same way a walk's path is fitted to its window.
//
// THE KERB THEY WALK TO IS ONE THEY CAN WALK TO (Khari, 2026-10-24, watching an
// episode: "walked through 2 buildings before arriving at a conversation"). The
// car used to stop at the nearest point of any line — often the street behind
// the block — and the walk from it was a straight line through the buildings.
// Now each end is the nearest point of a line with nothing built between it
// and the scene (the road map), and only failing that, the nearest point with a
// walked route to it.

const STEP = 2;          // resample the lines every 2 units
const JOIN = 2.5;        // two lines this close are one street (a junction)
const WALK_SPEED = 0.85;
const MAX_WALK_SHARE = 0.22;   // each walking leg takes at most this much of the window
const KERB_RADIUS = 30;        // how far to look for a reachable point on a line
const ACCEL = 0.18;            // share of the ride spent pulling away (and again pulling in)

let graph = null;

function resample(points) {
  const out = [];
  for (let i = 0; i < points.length - 1; i++) {
    const [ax, az] = points[i]; const [bx, bz] = points[i + 1];
    const L = Math.hypot(bx - ax, bz - az);
    const n = Math.max(1, Math.round(L / STEP));
    for (let k = 0; k < n; k++) out.push([ax + ((bx - ax) * k) / n, az + ((bz - az) * k) / n]);
  }
  out.push(points[points.length - 1]);
  return out;
}

function build() {
  const nodes = [];      // [x, z]
  const adj = [];        // [[j, w], …]
  const routeOf = [];
  CITY_ROUTES.forEach((r, ri) => {
    const pts = resample(r.points);
    pts.forEach((p, k) => {
      const i = nodes.length;
      nodes.push(p); adj.push([]); routeOf.push(ri);
      if (k > 0) {
        const j = i - 1; const w = Math.hypot(p[0] - nodes[j][0], p[1] - nodes[j][1]);
        adj[i].push([j, w]); adj[j].push([i, w]);
      }
    });
  });
  // Junctions: any two points of DIFFERENT lines this close are joined.
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      if (routeOf[i] === routeOf[j]) continue;
      const w = Math.hypot(nodes[i][0] - nodes[j][0], nodes[i][1] - nodes[j][1]);
      if (w < JOIN) { adj[i].push([j, w]); adj[j].push([i, w]); }
    }
  }
  return { nodes, adj };
}

function nearest(g, x, z) {
  let best = 0; let bd = Infinity;
  for (let i = 0; i < g.nodes.length; i++) {
    const d = (g.nodes[i][0] - x) ** 2 + (g.nodes[i][1] - z) ** 2;
    if (d < bd) { bd = d; best = i; }
  }
  return best;
}

/**
 * The nearest point on a line that someone at (x, z) can walk straight to:
 * nothing built in between. { i, clear } — `clear` false means none was found
 * within reach (or there is no road map yet) and `i` is simply the nearest.
 */
function kerbFor(g, x, z) {
  if (!roadMapBridge.map) return { i: nearest(g, x, z), clear: false };
  const near = [];
  for (let i = 0; i < g.nodes.length; i++) {
    const d = Math.hypot(g.nodes[i][0] - x, g.nodes[i][1] - z);
    if (d < KERB_RADIUS) near.push([d, i]);
  }
  near.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  for (const [, i] of near.slice(0, 80)) if (lineClear([x, z], g.nodes[i], 0.3)) return { i, clear: true };
  return { i: nearest(g, x, z), clear: false };
}

/** Dijkstra over a few hundred nodes; ties broken by index, so every viewer agrees. */
function shortest(g, a, b) {
  const n = g.nodes.length;
  const dist = new Float64Array(n).fill(Infinity); const prev = new Int32Array(n).fill(-1);
  const done = new Uint8Array(n);
  dist[a] = 0;
  for (;;) {
    let u = -1; let du = Infinity;
    for (let i = 0; i < n; i++) if (!done[i] && dist[i] < du) { du = dist[i]; u = i; }
    if (u < 0 || u === b) break;
    done[u] = 1;
    for (const [v, w] of g.adj[u]) if (du + w < dist[v]) { dist[v] = du + w; prev[v] = u; }
  }
  if (!Number.isFinite(dist[b])) return null;
  const path = [];
  for (let v = b; v >= 0; v = prev[v]) { path.push(g.nodes[v]); if (v === a) break; }
  return path.reverse();
}

const cache = new Map();
// A new road map (a shell stamped in) can change which kerb is reachable.
roadMapBridge.subscribe(() => cache.clear());

/** The three legs between two ground points: { foot1, car, foot2 } as point lists with lengths. */
export function driveRoute(from, to) {
  const key = `${from[0].toFixed(1)},${from[1].toFixed(1)}>${to[0].toFixed(1)},${to[1].toFixed(1)}`;
  const hit = cache.get(key);
  if (hit) return hit;
  if (!graph) graph = build();
  const ka = kerbFor(graph, from[0], from[1]); const kb = kerbFor(graph, to[0], to[1]);
  const a = ka.i; const b = kb.i;
  const car = (a === b ? [graph.nodes[a]] : shortest(graph, a, b)) || [graph.nodes[a], graph.nodes[b]];
  const leg = (pts) => {
    const cum = [0];
    for (let i = 1; i < pts.length; i++) cum.push(cum[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
    return { pts, cum, len: cum[cum.length - 1] };
  };
  // No line within reach in a straight walk: the walk to it goes round (cityPath).
  const foot = (p, q, clear) => (clear || !roadMapBridge.map ? leg([p, q]) : leg(routeBetween(p, q).pts));
  const out = { foot1: foot(from, car[0], ka.clear), car: leg(car), foot2: foot(car[car.length - 1], to, kb.clear) };
  cache.set(key, out);
  if (cache.size > 200) cache.delete(cache.keys().next().value);
  return out;
}

/** Share of the ride's distance covered at `u` (0..1 of its time): pull away, cruise, pull in. */
function ride(u) {
  const v = 1 / (1 - ACCEL);
  if (u < ACCEL) return { e: (v * u * u) / (2 * ACCEL), rate: (v * u) / ACCEL };
  if (u > 1 - ACCEL) { const w = 1 - u; return { e: 1 - (v * w * w) / (2 * ACCEL), rate: (v * w) / ACCEL }; }
  return { e: (v * ACCEL) / 2 + v * (u - ACCEL), rate: v };
}

function at(leg, d) {
  const { pts, cum, len } = leg;
  if (pts.length < 2 || len <= 0) return { x: pts[0][0], z: pts[0][1], heading: 0 };
  const dd = Math.max(0, Math.min(len, d));
  let i = 1; while (i < cum.length - 1 && cum[i] < dd) i++;
  const t = (dd - cum[i - 1]) / Math.max(1e-6, cum[i] - cum[i - 1]);
  const x = pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * t;
  const z = pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * t;
  return { x, z, heading: Math.atan2(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]) };
}

/**
 * Where someone on a drive is, `p` (0..1) through the window of `durMs`.
 * { x, z, heading, driving, moving, speed }. The car eases out and in.
 */
export function drivePos(route, p, durMs) {
  const secs = durMs / 1000;
  const w1 = Math.min(MAX_WALK_SHARE * secs, route.foot1.len / WALK_SPEED);
  const w2 = Math.min(MAX_WALK_SHARE * secs, route.foot2.len / WALK_SPEED);
  const wc = Math.max(1, secs - w1 - w2);
  const t = p * secs;
  if (t < w1) {
    const r = at(route.foot1, (t / Math.max(1e-6, w1)) * route.foot1.len);
    return { ...r, driving: false, moving: true, speed: route.foot1.len / Math.max(1e-6, w1) };
  }
  if (t < w1 + wc) {
    const { e, rate } = ride((t - w1) / wc);
    const r = at(route.car, e * route.car.len);
    return { ...r, driving: true, moving: true, speed: (route.car.len / wc) * Math.max(0.15, rate) };
  }
  const u = (t - w1 - wc) / Math.max(1e-6, w2);
  const r = at(route.foot2, u * route.foot2.len);
  return { ...r, driving: false, moving: u < 1, speed: route.foot2.len / Math.max(1e-6, w2) };
}
